/***
 * Función para crear una capa de tipo vector a partir de un archivo GeoJSON
 * @returns {VectorLayer} Capa de tipo vector
 * @example
 * // Ejemplo de uso
 * const layer = layerVector({title, url, params, cluster, iconColor}) // Retorna una capa de tipo vector
 *  
 */

import VectorLayer from 'ol/layer/Vector'
import { Vector as VectorSource } from 'ol/source.js';
import GeoJSON from 'ol/format/GeoJSON'
import { Icon, Style, Stroke, Fill } from 'ol/style.js';
import { Cluster } from 'ol/source.js';
import { getClusterStyle } from './utils';
import { boundingExtent } from 'ol/extent.js';
import { useToken } from '../../hooks/useToken';


/**
 * UTILS
 */
import { getSvg, isAppDomain, getVisorID } from '../../utils/misc';


/**
 * Create a custom loader with authentication
 * @param {string} url
 * @returns {function|null} Returns a loader function if authentication is available, null otherwise
 */
function createAuthenticatedLoader(url) {
    const { token } = useToken();

    if (!token || !isAppDomain(url)) {
        // If there is no token or the URL is not from the same domain, return null
        // The caller should handle this by not passing the loader property to VectorSource
        return null;
    }

    return async function (extent, resolution, projection) {
        try {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            const format = new GeoJSON();
            const features = format.readFeatures(data, {
                featureProjection: projection
            });

            // IMPORTANT: Add features to the source instead of returning them
            // OpenLayers custom loaders should add features to the source, not return them
            this.addFeatures(features);

        } catch (error) {
            console.error('❌ Error in custom loader:', error);
            throw error;
        }
    };
}

/**
 * Crea un vector Layer a partir de los datos de capa WFS geojson, 
 * agrupados o no en cluster según configuración de la capa en config.json
 * 
 * @param {Object} param0
 * @param {string} param0.title
 * @param {string} param0.url
 * @param {Object} param0.params
 * @param {Object} param0.cluster
 * @param {string} param0.iconColor
 * @param {Object} param0.map
 * @param {string} param0.nombre
 * @param {string} param0.category
 * @param {string} param0.iconPath
 * @param {boolean} param0.visible
 * @param {boolean} param0.queryable
 * 
 * @returns vectorLayer
 */
export function layerVector({ title, url, params, cluster, iconColor, map, nombre, category, iconPath, visible, queryable, attributions, minZoom, maxZoom }) {

    // Build params object, adding map_viewer_id automatically for incidence endpoints
    const finalParams = { ...params };
    
    // If this is an incidence endpoint, automatically add map_viewer_id
    if (url && url.includes('/api/incidence/')) {
        const mapViewerId = getVisorID();
        if (mapViewerId) {
            finalParams.map_viewer_id = mapViewerId;
        }
    }

    // Build query string, handling nested params like filters[status]
    // URLSearchParams handles bracket notation automatically (filters[status] -> filters%5Bstatus%5D)
    // which Laravel will correctly parse back to filters.status
    const queryString = new URLSearchParams(finalParams).toString();
    const separator = url.includes('?') ? '&' : '?';
    const fullUrl = `${url}${separator}${queryString}`;

    // Función para convertir 'rgb(r, g, b)' o HEX a RGBA
    const rgbaCache = new Map();
    const rgbToRgba = (color, opacity = 1) => {
        if (!color) return `rgba(0,0,0,${opacity})`;
        
        const key = `${color}-${opacity}`;
        if (rgbaCache.has(key)) return rgbaCache.get(key);

        let result;
        
        // Si es formato HEX (#72acdd o #abc)
        if (color.startsWith('#')) {
            let hex = color.slice(1);
            // Expandir formato corto (#abc -> #aabbcc)
            if (hex.length === 3) {
                hex = hex.split('').map(c => c + c).join('');
            }
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            result = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        } else {
            // Si es formato RGB rgb(r, g, b)
            const match = color.match(/\d+/g);
            if (match && match.length >= 3) {
                result = `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${opacity})`;
            } else {
                result = `rgba(0,0,0,${opacity})`;
            }
        }
        
        rgbaCache.set(key, result);
        return result;
    };

    // Crear loader personalizado con autenticación si es necesario
    const customLoader = createAuthenticatedLoader(fullUrl);

    // Se agrupará en cluster
    if (cluster) {
        const vectorSourceConfig = {
            url: fullUrl,
            format: new GeoJSON(),
            attributions: attributions || undefined,
        };

        // Solo agregar loader si no es null
        if (customLoader) {
            vectorSourceConfig.loader = customLoader;
        }

        const vectorSource = new VectorSource(vectorSourceConfig);

        const clusterSource = new Cluster({
            distance: parseInt(cluster.distance, 10),
            minDistance: parseInt(cluster.minDistance, 10),
            source: vectorSource,
            // Custom geometryFunction to only cluster Point/MultiPoint geometries
            // Non-point geometries are ignored (return null) to prevent errors
            geometryFunction: (feature) => {
                try {
                    const geometry = feature.getGeometry();
                    if (!geometry) return null;
                    
                    const geomType = geometry.getType();
                    
                    // Point geometries - return directly
                    if (geomType === 'Point') {
                        return geometry;
                    }
                    
                    // MultiPoint geometries - return first point
                    if (geomType === 'MultiPoint') {
                        const points = geometry.getPoints();
                        if (points && points.length > 0) {
                            return points[0];
                        }
                    }
                    
                    // Ignore non-Point geometries (polygons, lines, etc.)
                    return null;
                } catch (e) {
                    return null;
                }
            },
        });

        const clusterLayer = new VectorLayer({
            source: clusterSource,
            style: (feature) => getClusterStyle(feature, cluster.style, iconPath),
            title,
            visible,
            queryable,
            // minZoom/maxZoom control layer visibility based on map zoom level
            ...(minZoom !== null && minZoom !== undefined && { minZoom: parseFloat(minZoom) }),
            ...(maxZoom !== null && maxZoom !== undefined && { maxZoom: parseFloat(maxZoom) })
        });

        clusterLayer.set('category', category);  // Añadimos categoría indicada en el json a la capa de cluster
        clusterLayer.set('url', fullUrl);

        return clusterLayer;

    } else {
        let svgContent;
        const vectorSourceConfig = {
            url: fullUrl,
            format: new GeoJSON(),
            attributions: attributions || undefined,
        };

        // Solo agregar loader si no es null
        if (customLoader) {
            vectorSourceConfig.loader = customLoader;
        }

        const vectorSource = new VectorSource(vectorSourceConfig);

        const vectorLayer = new VectorLayer({
            source: vectorSource,
            // Personalización del estilo
            style: (feature) => {
                try {
                    const geometry = feature.getGeometry();
                    if (!geometry) {
                        return null;
                    }

                    const geometryType = geometry.getType();

                    if (geometryType === 'MultiPolygon' || geometryType === 'Polygon') {
                        return new Style({
                            stroke: new Stroke({
                                color: iconColor,
                                width: 3,
                            }),
                            fill: new Fill({
                                color: rgbToRgba(iconColor, 0.4), // Convierte a rgba con opacidad
                            }),
                        });
                    } else {
                        svgContent = getSvg(iconPath, iconColor);
                        const encodedSvg = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;

                        return new Style({
                            image: new Icon({
                                src: encodedSvg,
                                scale: 1.2, // Tamaño del icono
                            }),
                        });
                    }
                } catch (error) {
                    console.error('❌ Error styling feature:', error);
                    return null;
                }
            },
            // minZoom/maxZoom control layer visibility based on map zoom level
            ...(minZoom !== null && minZoom !== undefined && { minZoom: parseFloat(minZoom) }),
            ...(maxZoom !== null && maxZoom !== undefined && { maxZoom: parseFloat(maxZoom) })
        });

        vectorLayer.set('category', category);  // Añadimos categoría indicada en el json
        vectorLayer.set('title', title);
        vectorLayer.set('url', fullUrl);
        vectorLayer.set('queryable', queryable);

        return vectorLayer;
    }
}


/**
 * Añade el evento zoom sobre el cluster
 * @param {*} map 
 * @param {*} tileLayer 
 * @param {*} cluster 
 */
export function addZoomToClusterEvent(map, tileLayer, cluster) {
    if (cluster) {
        // Agregamos el evento de clic si hay clustering
        map.on('click', (event) => {
            tileLayer.getFeatures(event.pixel).then((clickedFeatures) => {
                if (clickedFeatures.length) {

                    // obtenemos los features
                    const features = clickedFeatures[0].get('features');
                    features.forEach((feature) => {
                        const geometry = feature.getGeometry();
                        const coordinates = geometry.getCoordinates();
                        const properties = feature.getProperties();

                        // console.log('Punto dentro del clúster:', layer.get('title'));
                        // console.log('Coordenadas:', coordinates);
                        // console.log('Propiedades:', properties);
                    });

                    // si contiene más de un features hace zoom
                    // Ajusta el padding y el zoom para evitar acercamientos excesivos
                    if (features.length > 1) {
                        const extent = boundingExtent(
                            features.map((r) => r.getGeometry().getCoordinates())
                        );
                        map.getView().fit(extent, {
                            duration: 1000,
                            padding: [50, 50, 50, 50],
                            maxZoom: 17 // Nivel de zoom maximo permitido
                        });
                    }
                }
            });




        });
    }
}
